import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useAuth } from "@clerk/tanstack-react-start";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, CircleStar, Clock, EyeOff, Lock, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

function getVisibility(group: {
  visibility?: string;
  isPrivate?: boolean;
}): "public" | "private" | "secret" {
  if (
    group.visibility === "public" ||
    group.visibility === "private" ||
    group.visibility === "secret"
  ) {
    return group.visibility;
  }
  return group.isPrivate ? "secret" : "public";
}

export const Route = createFileRoute("/invite/group/$token")({
  component: GroupInvitePage,
});

function GroupInvitePage() {
  const { token } = Route.useParams();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();

  const { data: invitation } = useSuspenseQuery(
    convexQuery(api.groupInvitations.getGroupInvitationByToken, { token }),
  );

  const { mutate: acceptInvitation, isPending: isAccepting } = useMutation({
    mutationFn: useConvexMutation(api.groupInvitations.acceptGroupInvitation),
    onSuccess: (result: { success: boolean; groupId: string } | undefined) => {
      if (result?.groupId) {
        navigate({ to: "/app/groups/$groupId", params: { groupId: result.groupId } });
      }
    },
  });

  // Invitation not found
  if (!invitation) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>
              This group invitation link is invalid or has been removed.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/app/groups" className={cn(buttonVariants({ variant: "outline" }))}>
              Browse Groups
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if invitation is expired
  const isExpired = invitation.expiresAt < Date.now();

  // Invitation expired
  if (isExpired || invitation.status === "expired") {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
              <Clock className="h-6 w-6 text-amber-500" />
            </div>
            <CardTitle>Invitation Expired</CardTitle>
            <CardDescription>
              This group invitation has expired. Please contact the group admin for a new invite.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/app/groups" className={cn(buttonVariants({ variant: "outline" }))}>
              Browse Groups
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invitation already accepted
  if (invitation.status === "accepted") {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            </div>
            <CardTitle>Already Accepted</CardTitle>
            <CardDescription>
              This invitation has already been accepted. You can view the group now.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link
              to="/app/groups/$groupId"
              params={{ groupId: invitation.group._id }}
              className={cn(buttonVariants())}
            >
              Go to Group
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invitation revoked
  if (invitation.status === "revoked") {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invitation Revoked</CardTitle>
            <CardDescription>
              This group invitation has been revoked by the group admin.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/app/groups" className={cn(buttonVariants({ variant: "outline" }))}>
              Browse Groups
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const visibility = getVisibility(invitation.group);
  const VisibilityIcon =
    visibility === "secret" ? EyeOff : visibility === "private" ? Lock : CircleStar;

  const inviterName = invitation.inviter
    ? `${invitation.inviter.firstName || ""} ${invitation.inviter.lastName || ""}`.trim()
    : "Someone";

  // Not signed in - prompt to sign in
  if (!isSignedIn) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <VisibilityIcon className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>{invitation.group.name}</CardTitle>
            {visibility !== "public" && (
              <div className="flex justify-center pt-1">
                <Badge variant="outline" className="text-xs">
                  {visibility === "secret" ? "Secret" : "Private"}
                </Badge>
              </div>
            )}
            <CardDescription>
              {invitation.group.description && (
                <span className="mt-2 block text-sm">{invitation.group.description}</span>
              )}
              <span className="mt-2 block text-sm">
                {inviterName} invited you to join this community.
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              Please sign in to accept this invitation.
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to="/sign-in"
                search={{ redirect: `/invite/group/${token}` }}
                className={cn(buttonVariants())}
              >
                Sign in
              </Link>
              <Link to="/sign-up" className={cn(buttonVariants({ variant: "outline" }))}>
                Create account
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Signed in - show accept button
  return (
    <div className="flex flex-1 items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <VisibilityIcon className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>{invitation.group.name}</CardTitle>
          {visibility !== "public" && (
            <div className="flex justify-center pt-1">
              <Badge variant="outline" className="text-xs">
                {visibility === "secret" ? "Secret" : "Private"}
              </Badge>
            </div>
          )}
          <CardDescription>
            {invitation.group.description && (
              <span className="mt-2 block text-sm">{invitation.group.description}</span>
            )}
            <span className="mt-2 block text-sm">
              {inviterName} invited you to join this community.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <div className="flex flex-col gap-2">
            <Button onClick={() => acceptInvitation({ token })} disabled={isAccepting}>
              {isAccepting ? "Accepting..." : "Accept Invitation"}
            </Button>
            <Link to="/app/groups" className={cn(buttonVariants({ variant: "outline" }))}>
              Maybe later
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
